import { isTieredVariant } from '@lub0v/santa-utils';
import { trackPremium } from '@src/utils/premiumAnalytics';
import { usePremiumOffer } from './PremiumOfferProvider';
import { useAppSelector } from '@src/state/hooks';
import { UsersIcon } from '@src/components/icons/UsersIcon';
import { StarIcon } from '@src/components/icons/StarIcon';
import { WrenchIcon } from '@src/components/icons/WrenchIcon';
import { QuestionIcon } from '@src/components/icons/QuestionIcon';
import { EyeOffIcon } from '@src/components/icons/EyeOffIcon';
import { MessageIcon } from '@src/components/icons/MessageIcon';
import { ClockIcon } from '@src/components/icons/ClockIcon';
import { LinkIcon } from '@src/components/icons/LinkIcon';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { HeartIcon } from '@src/components/icons/HeartIcon';
import { useEffect, useId, useState } from 'react';
import { Hint } from '@src/components';
import { useMatchesMedia } from '@src/components/layout/useMobileHeaderRecede';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { PremiumFeature, PremiumFeatures } from '@lub0v/santa-utils';
import { Shell } from '@src/components/Shell/Shell';
import { PageContent } from '@src/components/PageContent/PageContent';
import { isDotCom } from '@src/site';
import './PremiumTierPreview.css';

type Tier = { id: number; code: string; offerCode?: string; amountMinor: number; currency: string; features: PremiumFeatures };

export function PremiumTierPreview(props: { embedded?: boolean }) {
  return <PremiumTierPreviewContent key={isDotCom() ? 'com' : 'ru'} {...props} />;
}
function PremiumTierPreviewContent({ embedded = false }: { embedded?: boolean }) {
  const offer = usePremiumOffer();
  const mobile = useMatchesMedia('(max-width: 48rem)');
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const comparisonId = useId();
  const user = useAppSelector(state => state.session.user);
  const { t, i18n } = useTranslation();
  const currency = isDotCom() ? 'USD' : 'RUB';
  const [tiers, setTiers] = useState<Tier[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/premium/tiers', { signal: controller.signal, credentials: 'same-origin' })
      .then(async response => {
        if (!response.ok) throw new Error('Tiers unavailable');
        const data: Tier[] = await response.json();
        if (!Array.isArray(data) || data.length !== 3 || data.some((tier, index) =>
          tier.code !== `tier_${index + 1}` || tier.currency !== currency ||
          !Number.isSafeInteger(tier.amountMinor) || tier.amountMinor <= 0 || !tier.features
        )) throw new Error('Invalid tiers');
        if (!controller.signal.aborted) setTiers(data);
      }).catch(() => { if (!controller.signal.aborted) setFailed(true); });
    return () => controller.abort();
  }, [currency, attempt, offer.variant]);
  const price = (amount: number) => new Intl.NumberFormat(i18n.language, {
    style: 'currency', currency, maximumFractionDigits: 2,
    minimumFractionDigits: amount % 100 ? 2 : 0,
  }).format(amount / 100);
  const all = t('premiumTierPreview.allCurrentAndFutureThemes');
  const rows: { label: string; hint: string; icon: typeof HeartIcon; feature?: PremiumFeature; free: string | boolean; enabled?: string; paid?: boolean; heart?: boolean; ruSiteOnly?: boolean }[] = [
    { icon: UsersIcon, label: t('premiumTierPreview.participants'), hint: t('premiumTierPreview.participantsHint'), free: t('premiumTierPreview.unlimited') },
    { icon: StarIcon, label: t('premiumTierPreview.themes'), hint: t('premiumTierPreview.themesHint'), feature: 'boxThemes', free: t('premiumTierPreview.freeThemeCount'), enabled: all },
    { icon: WrenchIcon, label: t('premiumTierPreview.drawControls'), hint: t('premiumTierPreview.drawControlsHint'), feature: 'boxDraw', free: t('premiumTierPreview.basic'), enabled: t('premiumTierPreview.full') },
    { icon: QuestionIcon, label: t('premiumTierPreview.customQuestions'), hint: t('premiumTierPreview.questionsHint'), feature: 'boxQuestions', free: '1', enabled: t('premiumTierPreview.upTo10') },
    { icon: UsersIcon, label: t('premiumTierPreview.organizersPerGame'), hint: t('premiumTierPreview.organizersHint'), feature: 'boxMultiOrg', free: '1', enabled: t('premiumTierPreview.upTo3') },
    { ruSiteOnly: true, icon: EyeOffIcon, label: t('premiumTierPreview.noAdsForYouAndYourGames'), hint: t('premiumTierPreview.noAdsHint'), feature: 'adsDisabled', free: false },
    { icon: ClockIcon, label: t('premiumTierPreview.activityHistory'), hint: t('premiumTierPreview.activityHistoryHint'), feature: 'boxActivityLog', free: false },
    { icon: MessageIcon, label: t('premiumTierPreview.bulkMessages'), hint: t('premiumTierPreview.bulkMessagesHint'), feature: 'boxActivityLog', free: false },
    { icon: LinkIcon, label: t('premiumTierPreview.customGameAddress'), hint: t('premiumTierPreview.customAddressHint'), feature: 'boxDomain', free: false },
    { icon: HeartIcon, label: t('premiumTierPreview.supportSantaSecret'), heart: true, hint: t('premiumTierPreview.supportHint'), free: false, paid: true },
  ];
  // Mobile plans describe their actual benefits rather than repeating comparison cells.
  const mobileFeatures = (tier?: Tier) => rows.flatMap(row => {
    if (row.ruSiteOnly && isDotCom()) return [];
    const enabled = Boolean(tier && (row.feature ? tier.features[row.feature] : row.paid));
    if (!enabled && row.free === false) return [];
    let title = row.label;
    let description = row.hint;
    switch (row.feature) {
      case 'boxThemes':
        title = enabled ? all : t('premiumTierPreview.freeThemesTitle');
        break;
      case 'boxDraw':
        title = enabled ? t('premiumModal.rows.drawControl.title') : t('premiumTierPreview.basicDrawControls');
        description = enabled
          ? t('premiumTierPreview.fullDrawDescription')
          : t('premiumTierPreview.basicDrawDescription');
        break;
      case 'boxQuestions':
        title = enabled ? t('premiumTierPreview.upTo10CustomQuestions') : t('premiumTierPreview.freeQuestionsTitle');
        break;
      case 'boxMultiOrg':
        title = enabled ? t('premiumTierPreview.upTo3OrganizersPerGame') : t('premiumTierPreview.freeOrganizersTitle');
        description = enabled
          ? t('premiumTierPreview.paidOrganizersDescription')
          : t('premiumTierPreview.freeOrganizersDescription');
        break;
      default:
        if (!row.feature && !row.paid) title = t('premiumTierPreview.unlimitedParticipants');
    }
    return [{ ...row, title, description }];
  });
  const groups = [
    { title: t('premiumTierPreview.makeTheGameYours'), rows: rows.filter(row => (!row.ruSiteOnly || !isDotCom()) && typeof row.free === 'string') },
    { title: t('premiumTierPreview.aLittleMoreMagic'), rows: rows.filter(row => (!row.ruSiteOnly || !isDotCom()) && typeof row.free === 'boolean') },
  ];
  const cell = (value: string | boolean) => value === t('premiumTierPreview.unlimited') ? <span className="tier-preview__infinity" role="img" aria-label={t('premiumTierPreview.unlimitedParticipants')} title={t('premiumTierPreview.unlimitedParticipants')}>∞</span> : typeof value === 'string' ? value : <span className={value ? 'tier-check' : 'tier-empty'} aria-label={value ? t('premiumTierPreview.included') : t('premiumTierPreview.notIncluded')}>{value ? <span className="tier-check__gold" aria-hidden="true">✓</span> : '—'}</span>;
  const names = [t('premiumTierPreview.apprentice'), t('premiumTierPreview.wizard'), t('premiumTierPreview.santaMaster'), t('premiumTierPreview.santaGrandmaster')];
  const actionLabels = [t('premiumTierPreview.becomeWizard'), t('premiumTierPreview.becomeMaster'), t('premiumTierPreview.becomeMagister')];
  const descriptions = [
    t('premiumTierPreview.freeSummary'),
    isDotCom() ? t('premiumTierPreview.wizardSummary') : t('premiumTierPreview.wizardAdFreeSummary'),
    t('premiumTierPreview.masterSummary'),
    t('premiumTierPreview.magisterSummary'),
  ];
  const masterStatus = user?.premium?.offers.find(item => item.tier === 'tier_2')?.status;
  const recommendMaster = !['owned', 'included'].includes(masterStatus ?? '') && !['tier_2', 'tier_3', 'full'].includes(user?.premium?.access.tier ?? '');
  const tierAction = (tier: Tier, index: number) => {
    const access = isTieredVariant(user?.premium?.variant) ? user?.premium?.offers.find(item => item.tier === tier.code) : undefined;
    const status = access?.status;
    const upgradePrice = status === 'upgrade' && typeof access?.amountDueMinor === 'number' ? price(access.amountDueMinor) : null;
    if (status && !['purchase', 'upgrade'].includes(status)) return <span className="tier-preview__current" aria-current={status === 'owned' ? 'true' : undefined}>{status === 'owned' ? t('premiumTierPreview.yourCurrentPlan') : status === 'included' ? t('premiumTierPreview.includedInYourPlan') : t('premiumTierPreview.unavailable')}</span>;
    const action = <Link className="tier-preview__choose" to={`/premium/buy?tier=${tier.code}`} onClick={() => {
      const amountMinor = access?.amountDueMinor ?? tier.amountMinor;
      trackPremium('premium_unlock_clicked_v2', {
        feature: 'all', context: 'premium_page', placement: 'tier-comparison', action: 'open_buy_page',
        targetTier: tier.code, currentTier: user?.premium?.access.tier ?? 'free',
        purchaseType: status === 'upgrade' ? 'upgrade' : 'initial_purchase',
      }, { ...offer, ready: true, offerCode: tier.offerCode ?? tier.code, amountMinor, amount: amountMinor / 100, checkoutAmountMinor: undefined });
    }}>{upgradePrice === null ? actionLabels[index] : t('premiumTierPreview.upgradeAction', { action: actionLabels[index], price: upgradePrice })}</Link>;
    return upgradePrice === null ? action : <div className="tier-preview__upgrade-action">
      {action}
      <small className="tier-preview__credit-note">{t('premiumTierPreview.upgradeCreditNote')}</small>
    </div>;
  };
  const content = <div className={embedded ? "tier-preview tier-preview--embedded" : "tier-preview__panel"}>
      {!embedded && <>
      <p className="tier-preview__eyebrow">{t('premiumTierPreview.santaSecretPremium')}</p>
      <h1>{t('premiumTierPreview.aLittleMagicYourWay')}</h1>
      <p className="tier-preview__intro">{t('premiumTierPreview.intro')}</p>
      <p className="tier-preview__notice">{t('premiumTierPreview.previewNotice')}</p>
      </>}
      {failed ? <Hint tone="error">{t('premiumTierPreview.couldNotLoadPlans')} <button onClick={() => { setFailed(false); setTiers(null); setAttempt(value => value + 1); }}>{t('premiumTierPreview.tryAgain')}</button></Hint> : !tiers ? <p role="status">{t('premiumTierPreview.loadingPlans')}</p> : <>
        <p className="tier-preview__season-note">{t('premiumTierPreview.seasonNote')}</p>
        {!mobile ? (<div className="tier-preview__scroll" role="region" aria-label={t('premiumTierPreview.planComparison')} tabIndex={0}>
          <table className="tier-preview__table">
            <thead><tr><th scope="col">{t('premiumTierPreview.features')}</th>{names.map((name, index) => <th scope="col" key={name} className={index === 2 && recommendMaster ? 'tier-preview__recommended' : undefined}>
              {index === 2 && recommendMaster && <span className="tier-preview__recommendation">{t('premiumTierPreview.ourPick')}</span>}
              <div className="tier-preview__plan-header">
              <span className="tier-preview__plan-icon" aria-hidden="true">{index ? <CrownIcon /> : <HeartIcon />}</span>
              <span className="tier-preview__name">{name}</span>
              <strong className="tier-preview__price">{price(index ? tiers[index - 1].amountMinor : 0)}</strong>
              <p>{descriptions[index]}</p>
              {index > 0 && tierAction(tiers[index - 1], index - 1)}
              </div>
            </th>)}</tr></thead>
            {groups.map((group, groupIndex) => <tbody key={group.title}>
              {groupIndex === 0 && <tr className="tier-preview__group"><th colSpan={5} scope="rowgroup"><span>{group.title}</span></th></tr>}
              {group.rows.map(row => <tr key={row.label}>
                <th scope="row"><span className="tier-preview__feature-name"><span className="tier-preview__feature-icon" aria-hidden="true"><row.icon /></span>{row.label}</span><span className="tier-preview__feature-hint">{row.hint}</span></th>
                <td>{cell(row.free)}</td>{tiers.map(tier => <td key={tier.code}>{row.heart ? <span className="tier-support-heart" role="img" aria-label={t('premiumTierPreview.supportsSantaSecret')}><HeartIcon /></span> : cell(row.feature ? tier.features[row.feature] ? row.enabled ?? true : row.free : row.paid ?? row.free)}</td>)}
              </tr>)}
            </tbody>)}
            <tfoot><tr>
              <th scope="row"><span className="tier-preview__footer-label">{t('premiumTierPreview.yourNextStep')}</span></th>
              <td>{!user?.isPremium && <Link className="tier-preview__free" to="/box/new">{t('premiumTierPreview.playForFree')}</Link>}</td>
              {tiers.map((tier, index) => <td key={tier.code}>{tierAction(tier, index)}</td>)}
            </tr></tfoot>
          </table>
        </div>) : <div className="tier-preview__cards">
          {names.map((name, index) => {
            const tier = index ? tiers[index - 1] : undefined;
            const action = tier ? tierAction(tier, index - 1) : !user?.isPremium && <Link className="tier-preview__free" to="/box/new">{t('premiumTierPreview.playForFree')}</Link>;
            return <section key={name} className={`tier-preview__card tier-preview__card--${index}`}
              aria-labelledby={`${comparisonId}-title-${index}`}>
              <div className="tier-preview__card-top">
              {mobile && <h3 className="tier-preview__summary-heading" id={`${comparisonId}-title-${index}`}>
                <button type="button" className="tier-preview__summary" aria-expanded={selectedPlan === index}
                  aria-controls={`${comparisonId}-panel-${index}`} onClick={() => setSelectedPlan(selectedPlan === index ? null : index)}>
                  <span className="tier-preview__summary-top"><strong className="tier-preview__summary-name"><span aria-hidden="true">{index ? <CrownIcon /> : <HeartIcon />}</span>{name}</strong><strong>{price(tier?.amountMinor ?? 0)}</strong></span>
                  <span className="tier-preview__summary-bottom"><span>{descriptions[index]}</span><span aria-hidden="true">{selectedPlan === index ? '−' : '+'}</span></span>
                </button>
              </h3>}
              {tier && <div className="tier-preview__action tier-preview__summary-action">{action}</div>}
              </div>
              <div className="tier-preview__details" id={`${comparisonId}-panel-${index}`} hidden={mobile && selectedPlan !== index}>
              <ul className="tier-preview__features">
                {mobileFeatures(tier).map(row => <li key={row.label}>
                  <span className="tier-preview__feature-icon" aria-hidden="true"><row.icon /></span>
                  <div><strong>{row.title}</strong><p>{row.description}</p></div>
                </li>)}
              </ul>
              <footer className="tier-preview__action">{action}</footer>
              </div>
            </section>;
          })}
        </div>}
        <p className="tier-preview__footnote">{t('premiumTierPreview.footnote')}</p>
      </>}
    </div>;
  return embedded ? content : <Shell><PageContent className="tier-preview" testId="premium-tier-preview">{content}</PageContent></Shell>;
}
